import { FC, useState } from "react";
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  List,
  ListItemButton,
  Paper,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import SpeedIcon from "@mui/icons-material/Speed";
import { useTranslation } from "@/shared/hooks";
import { playerFooterControlPadding } from "./playerFooterControlPadding";
import type { VideoEngineLevel } from "./useVideoEngine";

const RATES = [0.5, 1, 1.25, 1.5, 2];

/** Badge shown next to the gear icon and in the dropdown, matching the
 * reference player's HD / HD+ / 4K resolution labels. */
function qualityBadge(height: number): string | null {
  if (height >= 2160) return "4K";
  if (height >= 1440) return "HD+";
  if (height >= 1080) return "HD";
  return null;
}

const dropdownPaperSx = {
  position: "absolute" as const,
  bottom: 60,
  right: -12,
  backgroundColor: "rgba(0,0,0,0.75)",
  overflow: "hidden",
};

/** Reference QualityButton/PlaybackSpeedButton badge: a compact rounded
 * label sitting at the top-right corner of the icon or the "1080p" text,
 * instead of MUI's default 20px circle that overlaps the label. */
const badgeSx = {
  "& .MuiBadge-badge": {
    top: 4,
    right: -5,
    height: 13,
    minWidth: 13,
    padding: "0 3px",
    borderRadius: "4px",
    fontSize: "0.55rem",
  },
};

const listItemSx = {
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.9rem",
  minHeight: 36,
  "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.2)" },
  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
};

/** Reference lifts the open menu above the neighbouring controls
 * (zIndex tooltip) so two dropdowns never paint over each other. */
const menuRootSx = (open: boolean) => ({
  position: "relative" as const,
  zIndex: (theme: { zIndex: { tooltip: number } }) =>
    open ? theme.zIndex.tooltip : "auto",
});

export const QualityMenu: FC<{
  levels: VideoEngineLevel[];
  currentLevel: number;
  autoLevel: boolean;
  onSetLevel: (index: number) => void;
}> = ({ levels, currentLevel, autoLevel, onSetLevel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (levels.length === 0) return null;

  const sortedLevels = [...levels].sort((a, b) => b.height - a.height);
  const current = !autoLevel
    ? sortedLevels.find((level) => level.index === currentLevel)
    : null;
  const currentBadge = current ? qualityBadge(current.height) : null;

  const select = (index: number) => {
    onSetLevel(index);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={menuRootSx(open)}>
        <IconButton
          size="large"
          onClick={() => setOpen((value) => !value)}
          sx={(theme) => ({
            color: "#fff",
            ...playerFooterControlPadding(theme),
          })}
          aria-label={t("player.controls.quality")}
        >
          {currentBadge ? (
            <Badge badgeContent={currentBadge} color="primary" sx={badgeSx}>
              <TuneIcon />
            </Badge>
          ) : (
            <TuneIcon />
          )}
        </IconButton>
        {open && (
          <Paper sx={dropdownPaperSx}>
            <List disablePadding dense>
              {sortedLevels.map((level) => {
                const badge = qualityBadge(level.height);
                return (
                  <ListItemButton
                    key={level.index}
                    selected={!autoLevel && currentLevel === level.index}
                    onClick={() => select(level.index)}
                    sx={listItemSx}
                  >
                    <Box sx={{ p: "2px" }}>
                      {badge ? (
                        <Badge
                          badgeContent={badge}
                          color="primary"
                          sx={badgeSx}
                        >
                          {level.height}p
                        </Badge>
                      ) : (
                        `${level.height}p`
                      )}
                    </Box>
                  </ListItemButton>
                );
              })}
              <ListItemButton
                selected={autoLevel}
                onClick={() => select(-1)}
                sx={listItemSx}
              >
                {t("player.controls.qualityAuto")}
              </ListItemButton>
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export const SpeedMenu: FC<{
  playbackRate: number;
  onSetRate: (rate: number) => void;
}> = ({ playbackRate, onSetRate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const showBadge = playbackRate !== 1;

  const select = (rate: number) => {
    onSetRate(rate);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={menuRootSx(open)}>
        <IconButton
          size="large"
          onClick={() => setOpen((value) => !value)}
          sx={(theme) => ({
            color: "#fff",
            ...playerFooterControlPadding(theme),
          })}
          aria-label={t("player.controls.speed")}
        >
          {showBadge ? (
            <Badge
              badgeContent={`${playbackRate}x`}
              color="primary"
              sx={badgeSx}
            >
              <SpeedIcon />
            </Badge>
          ) : (
            <SpeedIcon />
          )}
        </IconButton>
        {open && (
          <Paper sx={{ ...dropdownPaperSx, minWidth: 80 }}>
            <List disablePadding dense>
              {RATES.map((rate) => (
                <ListItemButton
                  key={rate}
                  selected={playbackRate === rate}
                  onClick={() => select(rate)}
                  sx={listItemSx}
                >
                  {rate === 1 ? t("player.controls.speedNormal") : `${rate}x`}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
